/**
 * The words of a piece of PROSE, folded exactly as a player's input is (D205).
 *
 * @remarks
 * The catalog side of one comparison. When a phrase's head resolves but a
 * modifier before it matches no authored adjective, the resolver asks
 * whether the entity's own description used that word — and the two sides
 * can only agree if the description was folded, split and tokenized by the
 * SAME stages the player's clause went through: `foldCharacters` (case,
 * typography, structural punctuation), `splitOnSeparators` (so a trailing
 * `lane.` is `lane`), then the single-space walk `tokenizeClause` makes.
 * A hyphen survives folding on both sides, so an authored `sea-grape tree`
 * meets a typed `sea-grape`; a numeral is compared canonical by the
 * resolver, not here, exactly as adjectives are (D133).
 *
 * The caller — the engine, once per bundle — hands the result to the
 * catalog as `ParseCandidateEntity.descriptionWords`. The parser itself
 * never reads a bundle; it is handed words.
 *
 * @packageDocumentation
 */

import { foldCharacters } from '../normalize/fold-characters.js';
import { splitOnSeparators } from './split-on-separators.js';
import { tokenizeClause } from './tokenize-clause.js';

/**
 * Every distinct word a piece of prose speaks, in first-appearance order.
 *
 * @param prose - Authored text, as written (any casing, any typography)
 * @returns The folded words, each once; empty for blank prose
 */
export const wordsOfProse = (prose: string): readonly string[] => {
	const words = new Set<string>();

	for (const clause of splitOnSeparators(foldCharacters(prose))) {
		for (const token of tokenizeClause(clause)) {
			words.add(token.text);
		}
	}

	return [...words];
};
